import React, { useState } from 'react';
import { CheckIcon } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { BounceHouseSelectionModal } from '../components/BounceHouseSelectionModal';
import { ErrorModal } from '../components/ErrorModal';
export const LuxurySoftPlayPackages = () => {
  const {
    addToCart
  } = useCart();
  const navigate = useNavigate();
  const [addedItems, setAddedItems] = useState<Set<string>>(new Set());
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [showBounceHouseModal, setShowBounceHouseModal] = useState(false);
  const [pendingPackage, setPendingPackage] = useState<any>(null);
  const handleAddToCart = (pkg: (typeof packages)[0]) => {
    // Special handling for Mega Fancy Tot - need to select bounce house first
    if (pkg.packageValue === 'mega-fancy-tot') {
      setPendingPackage(pkg);
      setShowBounceHouseModal(true);
      return;
    }
    // Try to add to cart
    const result = addToCart({
      type: 'softplay',
      packageName: pkg.name,
      packageValue: pkg.packageValue,
      price: pkg.price
    });
    if (result.success) {
      // Show success feedback briefly, then redirect to cart
      setAddedItems(prev => new Set(prev).add(pkg.packageValue));
      setErrorMessage('');
      // Redirect to cart after 800ms
      setTimeout(() => {
        navigate('/cart');
        window.scrollTo(0, 0);
      }, 800);
    } else {
      // Show error modal (no scroll)
      setErrorMessage(result.message || 'Unable to add item to cart');
    }
  };
  const handleBounceHouseSelection = (bounceHouse: string) => {
    if (!pendingPackage) return;
    const result = addToCart({
      type: 'softplay',
      packageName: pendingPackage.name,
      packageValue: pendingPackage.packageValue,
      price: pendingPackage.price,
      selectedBounceHouse: bounceHouse
    });
    if (result.success) {
      setAddedItems(prev => new Set(prev).add(pendingPackage.packageValue));
      setErrorMessage('');
      // Redirect to cart after 800ms
      setTimeout(() => {
        navigate('/cart');
        window.scrollTo(0, 0);
      }, 800);
    } else {
      // Show error modal (no scroll)
      setErrorMessage(result.message || 'Unable to add item to cart');
    }
    setPendingPackage(null);
  };
  const packages = [{
    id: '01',
    name: 'Fancy Tot',
    packageValue: 'fancy-tot',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/uU6ZBP89bA24T6gpbVzeEX/image.png',
    features: ['Medium White Bounce House w/ Ball Pit', 'White Puzzle Mat Area (15x12)', '3 Bouncy Animals', 'White Ball Pit (clear & white)', 'Gym Climbing Blocks', 'Crawling Tunnel', 'Safety Gates'],
    price: '$400'
  }, {
    id: '02',
    name: 'Mega Fancy Tot',
    packageValue: 'mega-fancy-tot',
    image: 'https://t3.ftcdn.net/jpg/02/77/69/26/360_F_277692690_xAGq27kZEjCmDq8D3lcttSjM6IeUVgKI.jpg',
    features: ['Your choice between two large white combo bounce houses', 'White Puzzle Mats (18ft x 12ft or 15ft x 15ft)', '5 Bouncy Animals', 'White Ball Pit (clear & white)', 'Gym Climbing Blocks', 'Crawling Tunnel', 'Safety Gates', 'Rocking Horse', '"My First Roller Coaster"'],
    price: '$475'
  }, {
    id: '03',
    name: 'Fancy Tot Customization Upgrades',
    packageValue: null,
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/goXZ8RzqHj1vHnv7UwdHh1/image.png',
    customizations: [{
      title: 'Standard Customizations - $25',
      items: ['Choose your ball colors', 'Choose your bouncy animal colors', 'Choose your mat color (blue, pink, or white)']
    }, {
      title: 'Vinyl Decals - $25 per decal',
      items: []
    }],
    price: null,
    isCustomization: true
  }];
  return <>
      <ErrorModal isOpen={!!errorMessage} message={errorMessage} onClose={() => setErrorMessage('')} data-id="element-775" />

      <div className="bg-white w-full" data-id="element-776">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20" data-id="element-777">
          {/* Page Title Section */}
          <div className="text-center mb-16" data-id="element-778">
            <h1 className="text-4xl md:text-5xl font-bold text-primary-500 leading-tight" data-id="element-779">
              Luxury Soft Play Packages
            </h1>
            <p className="mt-4 text-lg text-gray-600 max-w-3xl mx-auto" data-id="element-780">
              Elegant white-themed soft play setups that add a sophisticated
              touch to any celebration.
            </p>
          </div>

          {/* Package Cards Section */}
          <div className="space-y-8" data-id="element-781">
            {packages.map(pkg => <div key={pkg.id} className="bg-white rounded-3xl shadow-lg overflow-hidden border border-gray-100" data-id="element-782">
                <div className="flex flex-col md:flex-row" data-id="element-783">
                  {/* Package Image */}
                  <div className="md:w-1/2 relative" data-id="element-784">
                    <img src={pkg.image} alt={`${pkg.name} Package`} className="w-full h-full object-cover" data-id="element-785" />
                    {pkg.id === '01' && <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-70 text-white text-center py-2 text-sm" data-id="element-786">
                        Color customizations, as shown here, are available for
                        an additional fee. See details below.
                      </div>}
                  </div>

                  {/* Package Details */}
                  <div className="p-8 md:w-1/2" data-id="element-787">
                    <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-788">
                      <span className="text-primary-500" data-id="element-789">{pkg.id}</span> -{' '}
                      {pkg.name}
                    </h2>
                    {pkg.isCustomization ? <>
                        <p className="text-gray-600 mb-6" data-id="element-790">
                          Personalize your Fancy Tot package with these
                          customization options:
                        </p>
                        <div className="space-y-4 mb-8" data-id="element-791">
                          {pkg.customizations.map((customization, idx) => <div key={idx} data-id="element-792">
                              <p className="font-bold text-gray-900 mb-2" data-id="element-793">
                                {customization.title}
                              </p>
                              {customization.items.length > 0 && <ul className="ml-6 space-y-1" data-id="element-794">
                                  {customization.items.map((item, itemIdx) => <li key={itemIdx} className="text-gray-600 list-disc" data-id="element-795">
                                      {item}
                                    </li>)}
                                </ul>}
                            </div>)}
                        </div>
                      </> : <ul className="space-y-3 mb-8" data-id="element-796">
                        {pkg.features.map((feature, idx) => <li key={idx} className="flex items-start" data-id="element-797">
                            <CheckIcon className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-798" />
                            <span data-id="element-799">{feature}</span>
                          </li>)}
                      </ul>}
                    {pkg.price && <div className="mt-auto text-center border-t border-gray-100 pt-6" data-id="element-800">
                        <span className="text-4xl font-bold text-green-800 block mb-4" data-id="element-801">
                          {pkg.price}
                        </span>
                        <button onClick={() => handleAddToCart(pkg)} className={`inline-flex items-center px-6 py-3 rounded-md font-medium transition-all shadow-md ${addedItems.has(pkg.packageValue) ? 'bg-green-500 text-white' : 'bg-secondary-500 text-white hover:bg-secondary-600'}`} data-id="element-802">
                          {addedItems.has(pkg.packageValue) ? '✓ Added to Cart' : 'Add to Cart'}
                        </button>
                      </div>}
                  </div>
                </div>
              </div>)}
          </div>
        </div>

        {/* Bounce House Selection Modal for Mega Fancy Tot */}
        <BounceHouseSelectionModal isOpen={showBounceHouseModal} onClose={() => {
        setShowBounceHouseModal(false);
        setPendingPackage(null);
      }} onSelect={handleBounceHouseSelection} packageName="Mega Fancy Tot" data-id="element-803" />
      </div>
    </>;
};